import { decimal } from "../../utils/DecimalPrecision";
import { IMeasurementData } from "../interfaces";
import ContractRowModel from "./ContractRowModel";
import WBSRowModel from "./WBSRowModel";

const defaultData = { total: 0, quantity: 0, percentual: 0 };
const defaultMeasured = { total: 0 };

class MeasurementRowModel extends WBSRowModel {
  public readonly contractUid: string;
  public readonly measurementUid: string;
  public readonly createdAt: Date;
  public modifiedAt: Date;

  private _rowData: ContractRowModel | undefined = undefined;
  private _percentual: number = 0;
  private _measured: IMeasurementData = { ...defaultData };
  private _currentToBilling: IMeasurementData = { ...defaultData };

  /** Propriedade para verificar o status dos dados ao fazer os cálculos */
  private _status: "blank" | "no-change" | "changed" | "saving" | "saved";
  /** Propriedade que guarda o estado original do measured para recálculo dessa MeasurementRow */
  private _measuredOld: { total: number; quantity?: number } = {
    ...defaultMeasured,
  };

  constructor(
    uid: string,
    contractUid: string = "",
    measurementUid: string = "",
    structured: string = "",
    rowType: number = 0,
    total: number = 0,
    quantity: number = 0,
    unitCost: number = 0,
    status = "blank",
    createdAt = new Date(),
    modifiedAt = new Date()
  ) {
    super(uid, structured, rowType, quantity, unitCost, total);

    this.contractUid = contractUid;
    this.measurementUid = measurementUid;
    this._status = status as any;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
  }

  /** Linha do contrato vinculada a esta. */
  get contractRow(): ContractRowModel | undefined {
    return this._rowData;
  }

  /** Valores medidos acumulados da linha. */
  get measured(): IMeasurementData {
    return { ...this._measured };
  }

  /** Valores a faturar da linha. */
  get currentToBilling(): IMeasurementData {
    return { ...this._currentToBilling };
  }

  /** Valores medidos da linha. */
  get currentMeasurement(): IMeasurementData {
    return {
      total: this.total,
      quantity: this.quantity,
      percentual: this._percentual,
    };
  }

  /** Porcentagem da linha. */
  get percentual(): number {
    return this._percentual;
  }

  /** Se a linha já veio preenchida do banco ou foi criada em branco. */
  get isFilled() {
    return this._status !== "blank";
  }

  /** Se a linha foi alterada.
   * (Aqui é considerado também se a linha está sendo salva) */
  get isChanged() {
    return this._status === "changed" || this._status === "saving";
  }

  /** Verifica se a linha atual pode ser exibida nas tabelas.
   * Se a linha do contrato foi cancelada - teve seu valor total
   * distratado - e nenhuma medição foi feita com a mesma
   * é retornado falso.
   */
  canShowAt(page: "contract" | "measurement") {
    if (!this._rowData) return false;
    return this._rowData.canShowAt(page);
  }

  /** Vincula a linha do contrato referente a esta
   * linha da medição e atualiza os valores acumulados e
   * a faturar.
   */
  public attachContractRow(row: ContractRowModel) {
    if (!row || row.structured !== this.structured) return;
    this._rowData = row;

    // Obter dados da measured antiga
    if (this.isFilled) this.setRowOldMeasured(row.getMeasured());
  }

  /** Define que os valores da linha foram alterados em memória. */
  setRowStatusToChanged() {
    this._status = "changed";

    if (this._rowData) {
      this.setRowOldMeasured(this._rowData.getMeasured());
    }
  }

  /** Define que as alterações da linha estão sendo salvas no banco. */
  setRowStatusToSaving() {
    this._status = "saving";
  }

  /** Define que as alterações da linha foram salvas no banco. */
  setRowStatusToSaved() {
    if (this._status === "saving") this._status = "saved";
  }

  /** Define os valores atuais da linha medida. */
  setCurrentMeasurement(current: IMeasurementData) {
    this.total = current.total;
    this.quantity = current.quantity;
    this._percentual = current.percentual;
  }

  /** Define os valores acumulados da linha medida. */
  setAccumulatedMeasurement(accumulated: IMeasurementData) {
    this._measured = accumulated;
  }

  /** Define os valores de saldo a medir da linha medida. */
  setBillingMeasurement(billing: IMeasurementData) {
    this._currentToBilling = billing;
  }

  /** Altera a propriedade de _measuredOld com os valores acumulados antigos/originais,
   * ou seja, subtraindo os valores da medição atual dos valores acumulados passado
   * por parâmetro.
   */
  private setRowOldMeasured(measured) {
    this._measuredOld = {
      total: decimal(measured.total).subtract(this.total).toNumber(),
      quantity: measured.quantity
        ? decimal(measured.quantity).subtract(this.quantity).toNumber()
        : 0,
    };
  }

  /** Obtém os valores medidos acumulados da linha, caso a linha já esteja preenchida
   * pega os valores acumulados antigos, caso seja uma linha em branco pega
   * os valores acumulados da linha do contrato.
   */
  public getRowMeasuredWithoutCurrentMeasurement() {
    if (!this._rowData) return { total: 0 };
    return this.isFilled ? this._measuredOld : this._rowData.getMeasured();
  }

  /** Monta um objeto JSON com as propriedades necessárias para salvar
   * no banco de dados. */
  toSave() {
    const rowExtraData = this.isStage
      ? {}
      : {
          quantity: this.quantity,
        };

    return {
      ...rowExtraData,
      structured: this.structured,
      rowType: this.rowType,
      total: this.total,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
    };
  }

  /** Criar a instância de uma linha da medição com os dados informados. */
  static create(
    uid: string,
    data: Partial<MeasurementRowModel>
  ): MeasurementRowModel {
    return new MeasurementRowModel(
      uid,
      data.contractUid,
      data.measurementUid,
      data.structured,
      data.rowType,
      data.total,
      data.quantity,
      data.unitCost,
      "no-change",
      data.createdAt,
      data.modifiedAt
    );
  }

  /** Criar a instância de uma linha da medição em branco. */
  static createBlank(
    uid: string,
    data: Partial<MeasurementRowModel>
  ): MeasurementRowModel {
    return new MeasurementRowModel(
      uid,
      data.contractUid,
      data.measurementUid,
      data.structured,
      data.rowType,
      0,
      0,
      0,
      "blank",
      undefined,
      undefined
    );
  }
}

export default MeasurementRowModel;
